/**
 * PublishReadinessHost (web) — single, app-root mount of the
 * PublishReadinessModal. Twin of PaywallHost + mobile's
 * PublishReadinessHost.
 *
 * Subscribes to the global publish-readiness bus
 * (`lib/publishReadiness.ts`) and pops the modal whenever something
 * requests it: the /home toggle handler calling
 * `requestPublishReadiness()` after its pre-check fails, OR the
 * convex-client interceptor in `convexDispatch.ts` auto-firing on a
 * NOT_PUBLISH_READY ConvexError.
 *
 * Owns the reactive `publishReadiness` query so neither emitter has to
 * carry the rule list, and so the checkboxes live-update as the user
 * satisfies rules. Auto-dismisses once every rule passes.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConvexAuth, useQuery } from 'convex/react';

import { api as convexApi } from '../../../convex/functions/_generated/api';
import {
  PublishReadinessModal,
  type PublishRule,
} from './PublishReadinessModal';
import { subscribeToPublishReadiness } from '@/lib/publishReadiness';

export function PublishReadinessHost() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useConvexAuth();
  const authedRef = useRef(isAuthenticated);
  useEffect(() => {
    authedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const readiness = useQuery(
    convexApi.profiles.publishReadiness,
    isAuthenticated ? {} : 'skip',
  ) as { canPublish: boolean; rules: PublishRule[] } | undefined;

  useEffect(() => {
    return subscribeToPublishReadiness(() => {
      if (!authedRef.current) return;
      setOpen(true);
    });
  }, []);

  // Auto-dismiss once the profile is publish-ready.
  useEffect(() => {
    if (open && readiness?.canPublish) setOpen(false);
  }, [open, readiness?.canPublish]);

  return (
    <PublishReadinessModal
      open={open}
      rules={readiness?.rules ?? []}
      onOpenBuilder={() => {
        setOpen(false);
        navigate('/builder');
      }}
      onClose={() => setOpen(false)}
    />
  );
}
